import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ModalClientRegistration from "./ModalClientRegistration";
import DatePresetPicker from "./DatePresetPicker";
import { getPurchaseItems } from "../utils/purchaseItems";
import { notify } from '../utils/alertBus';
import useConfirmDialog from '../hooks/useConfirmDialog';

const PaymentModal = ({
    isOpen,
    onClose,
    onConfirmPayment,
    purchaseData,
    canReadClient = true,
    canCreateClient = true,
}) => {
    const [purchase, setPurchase] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState("");
    const [paymentDetails, setPaymentDetails] = useState({});
    const [documentType, setDocumentType] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [pendingDocumentType, setPendingDocumentType] = useState(null);
    const [showClientModal, setShowClientModal] = useState(false);
    const [name, setName] = useState("");
    const [rut, setRut] = useState("");
    const [email, setEmail] = useState("");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [phone, setPhone] = useState("");
    const [clientType, setClientType] = useState("");
    const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');

    const navigate = useNavigate();
    const { confirm } = useConfirmDialog();

    const normalizeDocumentType = (value) => {
        const normalized = String(value || '').trim().toLowerCase();
        if (normalized === 'boleta' || normalized === 'factura') {
            return normalized;
        }
        return null;
    };

    useEffect(() => {
        setPurchase(purchaseData);
        setDocumentType(normalizeDocumentType(purchaseData?.documentType));
        const nextClient = purchaseData?.Client || null;
        setSelectedClient(nextClient);
        setName(nextClient?.name || "");
        setRut(nextClient?.rut || "");
        setEmail(nextClient?.email || "");
        setAddress(nextClient?.address || "");
        setCity(nextClient?.city || "");
        setPhone(nextClient?.phone || "");
        setClientType("");
        setPendingDocumentType(null);
    }, [purchaseData]);

    // Function to reset payment details when payment method changes
    const resetPaymentDetails = (nextDetails = {}) => {
        setPaymentDetails(nextDetails); // Reset all previous inputs
    };

    const paymentMethods = [
        { id: "tarjeta", label: "Tarjeta" },
        { id: "transferencia", label: "Transferencia" },
        { id: "cheque", label: "Cheque" },
        { id: "crédito", label: "Crédito" },
        { id: "efectivo", label: "Efectivo" },
    ];

    const documentTypeOptions = [
        { id: 'boleta', label: 'Boleta', requiresClient: false },
        { id: 'factura', label: 'Factura', requiresClient: true },
    ];

    const handleDocumentTypeClick = (option) => {
        if (option.requiresClient && !selectedClient) {
            setPendingDocumentType(option.id);
            setShowClientModal(true);
            return;
        }
        setDocumentType(option.id);
    };

    const handleClientPicked = (client) => {
        const resolvedClient = client || null;
        setSelectedClient(resolvedClient);
        setName(resolvedClient?.name || "");
        setRut(resolvedClient?.rut || "");
        setEmail(resolvedClient?.email || "");
        setAddress(resolvedClient?.address || "");
        setCity(resolvedClient?.city || "");
        setPhone(resolvedClient?.phone || "");
        setClientType("");
        if (pendingDocumentType) {
            setDocumentType(pendingDocumentType);
            setPendingDocumentType(null);
        }
    };

    const handleCloseClientPicker = (open) => {
        setShowClientModal(open);
        if (!open) {
            setPendingDocumentType(null);
        }
    };

    const handleMethodButtonClick = (methodId) => {
        if (paymentMethod === methodId) return;
        setPaymentMethod(methodId);
        if (methodId === "crédito") {
            resetPaymentDetails({
                creditDueDate: "",
                initialPayment: 0,
                initialPaymentMethod: "efectivo",
            });
            return;
        }
        resetPaymentDetails(); // Reset details when payment method changes
    };

    const handleConfirm = async () => {
        if (!paymentMethod) {
            notify("Seleccione un método de pago.");
            return;
        }

        const total = Number(purchase?.totalAmount || 0);
        const paidAmount = Number(purchase?.paidAmount || 0);
        const pendingAmount =
            purchase?.balance !== undefined
                ? Number(purchase.balance || 0)
                : Math.max(total - paidAmount, 0);
        const expectedAmount = pendingAmount > 0 ? pendingAmount : total;

        if (paymentMethod === "transferencia" || paymentMethod === "tarjeta") {
            if (!paymentDetails.transactionNumber) {
                notify("Ingrese el número de transacción.");
                return;
            }
        }

        if (paymentMethod === "cheque") {
            if (!paymentDetails.chequeNumber) {
                notify("Ingrese el número de cheque.");
                return;
            }
        }

        if (paymentMethod === "efectivo") {
            const received = Number(paymentDetails.receivedAmount || 0);

            if (!received || received <= 0) {
                notify("Ingrese el monto recibido en efectivo.");
                return;
            }

            if (received < expectedAmount) {
                const proceed = await confirm({
                    title: 'Monto insuficiente',
                    message: 'El monto recibido es menor que el total. ¿Desea continuar?',
                    confirmLabel: 'Continuar',
                    cancelLabel: 'Cancelar',
                    tone: 'warning',
                });
                if (!proceed) return;
            }
        }

        if (paymentMethod === "crédito") {
            if (!paymentDetails.creditDueDate) {
                notify("Ingrese la fecha de vencimiento del crédito.");
                return;
            }
            const initialPayment = Number(paymentDetails.initialPayment || 0);
            if (!Number.isFinite(initialPayment) || initialPayment < 0) {
                notify("El abono inicial debe ser 0 o mayor.");
                return;
            }
            if (initialPayment > 0 && !paymentDetails.initialPaymentMethod) {
                notify("Seleccione el método del abono inicial.");
                return;
            }
        }

        if (documentType === 'factura' && !selectedClient) {
            notify('Factura requiere cliente.');
            return;
        }

        const paymentTerm = paymentMethod === "crédito" ? "credit" : "immediate";
        const resolvedClientId = selectedClient?.id ?? purchase?.clientId ?? null;
        onConfirmPayment(
            paymentMethod,
            paymentDetails,
            paymentTerm,
            documentType,
            resolvedClientId
        );
        onClose(); // Close the modal after confirmation
    };

    const totalAmount = purchase?.totalAmount ?? 0;
    const paidAmount = Number(purchase?.paidAmount || 0);
    const pendingAmount =
        purchase?.balance !== undefined
            ? Number(purchase.balance || 0)
            : Math.max(totalAmount - paidAmount, 0);
    const expectedAmount = pendingAmount > 0 ? pendingAmount : totalAmount;
    const purchaseItems = getPurchaseItems(purchase);
    const itemsSubtotal = purchaseItems.length
        ? purchaseItems.reduce((sum, item) => {
              if (!item || item.quantity <= 0) return sum;
              const unit = (item.price || 0) - (item.discount || 0);
              return sum + Math.floor(unit * item.quantity);
          }, 0)
        : totalAmount;
    const orderDiscountAmountNum = Number(purchase?.orderDiscountAmount);
    const orderDiscountAmountRaw = Number.isFinite(orderDiscountAmountNum)
        ? orderDiscountAmountNum
        : null;
    const orderDiscountPercentNum = Number(purchase?.orderDiscountPercentage);
    const orderDiscountPercentage = Number.isFinite(orderDiscountPercentNum)
        ? orderDiscountPercentNum
        : null;
    const effectiveOrderDiscount =
        typeof orderDiscountPercentage === 'number'
            ? Math.floor(itemsSubtotal * (orderDiscountPercentage / 100))
            : orderDiscountAmountRaw || 0;

        const client = selectedClient;
        const initialPaymentMethods = paymentMethods.filter((method) => method.id !== "crédito");
        const docLabel = (() => {
            if (documentType === 'boleta' || documentType === 'factura') {
                return documentType.charAt(0).toUpperCase() + documentType.slice(1);
        }
        return 'Factura';
    })();

    return (
        isOpen && (
            <>
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={onClose}
                >
                    <div
                        className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                    <h2 className="text-xl font-bold mb-2">Registrar Pago</h2>
                    <div className="mb-3 text-sm bg-gray-50 border rounded p-3">
                        <p className="font-semibold">
                            {docLabel} N° {purchase?.id || '—'}
                        </p>
                        {client ? (
                            <div className="mt-1 space-y-0.5">
                                <p><span className="font-medium">Cliente:</span> {client.name || 'Sin nombre'}</p>
                                {client.rut ? <p><span className="font-medium">RUT:</span> {client.rut}</p> : null}
                                {client.email ? <p><span className="font-medium">Email:</span> {client.email}</p> : null}
                                {client.phone ? <p><span className="font-medium">Teléfono:</span> {client.phone}</p> : null}
                                {client.address ? <p><span className="font-medium">Dirección:</span> {client.address}{client.city ? `, ${client.city}` : ''}</p> : null}
                            </div>
                        ) : (
                            <p className="text-gray-700 mt-1">Venta detalle (sin cliente)</p>
                        )}
                        <div className="mt-2">
                            <button
                                type="button"
                                onClick={() => setShowClientModal(true)}
                                className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                                disabled={!canReadClient && !canCreateClient}
                            >
                                {client ? 'Cambiar cliente' : 'Seleccionar cliente'}
                            </button>
                            {!canReadClient && !canCreateClient && (
                                <p className="text-xs text-red-600 mt-1">
                                    No tienes permisos para buscar clientes.
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="mb-2 text-sm">
                        <p className="font-medium">
                            Subtotal: ${itemsSubtotal.toLocaleString('de-DE')}
                        </p>
                        {effectiveOrderDiscount > 0 && (
                            <p className="text-red-600">
                                Descuento orden
                                {orderDiscountPercentage != null
                                    ? ` (${orderDiscountPercentage}%)`
                                    : ''}: -${effectiveOrderDiscount.toLocaleString('de-DE')}
                            </p>
                        )}
                        <p className="font-semibold mt-1">
                            Total: ${totalAmount.toLocaleString('de-DE')}
                        </p>
                        {pendingAmount > 0 && (
                            <p className="font-semibold mt-1 text-amber-700">
                                Pendiente: ${pendingAmount.toLocaleString('de-DE')}
                            </p>
                        )}
                    </div>

                    <p className="block text-sm font-medium mb-2 mt-4">Documento:</p>
                    <div className="flex flex-wrap gap-2 mb-2">
                        {documentTypeOptions.map((option) => {
                            const disabled = option.requiresClient && !client;
                            return (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => handleDocumentTypeClick(option)}
                                    className={`px-3 py-1 text-sm rounded border transition-colors ${
                                        documentType === option.id
                                            ? "bg-emerald-500 text-white border-emerald-500"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                    } ${disabled ? "opacity-60" : ""}`}
                                    aria-disabled={disabled}
                                >
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                    {!client && (
                        <p className="text-xs text-gray-500 mb-3">
                            Factura requiere cliente.
                        </p>
                    )}

                    {/* Payment Method Buttons */}
                    <p className="block text-sm font-medium mb-2 mt-4">Método de Pago:</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                        {paymentMethods.map((method) => (
                            <button
                                key={method.id}
                                type="button"
                                onClick={() => handleMethodButtonClick(method.id)}
                                className={`px-3 py-1 text-sm rounded border transition-colors ${
                                    paymentMethod === method.id
                                        ? "bg-blue-500 text-white border-blue-500"
                                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                }`}
                            >
                                {method.label}
                            </button>
                        ))}
                    </div>

                    {/* Conditional Inputs Based on Payment Method */}
                    {paymentMethod === "cheque" && (
                        <div className="mt-4 space-y-2">
                            <label className="block text-sm font-medium">Número de Cheque:</label>
                            <input
                                type="text"
                                className="w-full border rounded p-2"
                                value={paymentDetails.chequeNumber || ""}
                                onChange={(e) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        chequeNumber: e.target.value,
                                    })
                                }
                            />
                            <label className="block text-sm font-medium">Fecha de Expiración:</label>
                            <input
                                type="date"
                                className="w-full border rounded p-2"
                                value={paymentDetails.chequeExpiration || ""}
                                onChange={(e) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        chequeExpiration: e.target.value,
                                    })
                                }
                            />
                            <label className="block text-sm font-medium">Abono Inicial:</label>
                            <input
                                type="number"
                                className="w-full border rounded p-2"
                                min="0"
                                step="1"
                                value={paymentDetails.initialPayment ?? 0}
                                onChange={(e) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        initialPayment: Math.max(0, parseInt(e.target.value, 10) || 0),
                                    })
                                }
                            />
                        </div>
                    )}

                    {paymentMethod === "transferencia" && (
                        <div className="mt-4 space-y-2">
                            <label className="block text-sm font-medium">Número de Transacción:</label>
                            <input
                                type="text"
                                className="w-full border rounded p-2"
                                value={paymentDetails.transactionNumber || ""}
                                onChange={(e) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        transactionNumber: e.target.value,
                                    })
                                }
                            />
                        </div>
                    )}

                    {paymentMethod === "efectivo" && (
                        <div className="mt-4 space-y-2">
                            <label className="block text-sm font-medium">Monto Recibido:</label>
                            <input
                                type="number"
                                className="w-full border rounded p-2"
                                value={paymentDetails.receivedAmount || ""}
                                onChange={(e) => {
                                    const receivedAmount = parseInt(e.target.value, 10) || 0;
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        receivedAmount,
                                    });
                                }}
                            />
                            <p className="text-sm font-semibold">
                                {paymentDetails.receivedAmount - expectedAmount > 0
                                    ? `Vuelto: ${(paymentDetails.receivedAmount - expectedAmount).toLocaleString('de-DE')}`
                                    : `Faltan: ${Math.abs(
                                          expectedAmount - (paymentDetails.receivedAmount || 0)
                                      ).toLocaleString('de-DE')}`}
                            </p>
                        </div>
                    )}

                    {paymentMethod === "crédito" && (
                        <div className="mt-4 space-y-2">
                            <DatePresetPicker
                                label="Fecha de vencimiento del crédito"
                                presetLabel="Plazo"
                                inputLabel="Fecha personalizada"
                                presetOptionLabel={(days) => `En ${days} dias`}
                                value={paymentDetails.creditDueDate || ""}
                                onChange={(value) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        creditDueDate: value,
                                    })
                                }
                                containerClassName="w-full"
                                labelClassName="block text-sm font-medium mb-1"
                                inputLabelClassName="block text-xs text-gray-600 mb-1"
                                selectClassName="w-full border rounded p-2"
                                inputClassName="w-full border rounded p-2"
                            />
                            <label className="block text-sm font-medium">Abono Inicial:</label>
                            <input
                                type="number"
                                className="w-full border rounded p-2"
                                min="0"
                                step="1"
                                value={paymentDetails.initialPayment ?? 0}
                                onChange={(e) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        initialPayment: Math.max(0, parseInt(e.target.value, 10) || 0),
                                    })
                                }
                            />
                            <label className="block text-sm font-medium">Método del abono inicial:</label>
                            <select
                                className="w-full border rounded p-2"
                                value={paymentDetails.initialPaymentMethod || "efectivo"}
                                onChange={(e) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        initialPaymentMethod: e.target.value,
                                    })
                                }
                            >
                                {initialPaymentMethods.map((method) => (
                                    <option key={method.id} value={method.id}>
                                        {method.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {paymentMethod === "tarjeta" && (
                        <div className="mt-4 space-y-2">
                            <label className="block text-sm font-medium">Número de Transacción:</label>
                            <input
                                type="text"
                                className="w-full border rounded p-2"
                                value={paymentDetails.transactionNumber || ""}
                                onChange={(e) =>
                                    setPaymentDetails({
                                        ...paymentDetails,
                                        transactionNumber: e.target.value,
                                    })
                                }
                            />
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="flex justify-end gap-4">
                        <button
                            onClick={() => {
                                if (!purchase?.id) return;
                                navigate(`/branches/${branchId}/purchases/${purchase.id}/edit`);
                            }}
                            className="px-4 py-2 border border-gray-300 rounded bg-gray-200 hover:bg-gray-300"
                        >
                            Continuar Compra
                        </button>
                        <button
                            onClick={handleConfirm}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                            Confirmar Pago
                        </button>
                    </div>
                    </div>
                </div>
                <ModalClientRegistration
                    isOpen={showClientModal}
                    onClose={handleCloseClientPicker}
                    name={name}
                    setName={setName}
                    setClientId={() => {}}
                    rut={rut}
                    setRut={setRut}
                    email={email}
                    setEmail={setEmail}
                    address={address}
                    setAddress={setAddress}
                    city={city}
                    setCity={setCity}
                    phone={phone}
                    setPhone={setPhone}
                    savePurchase={() => {}}
                    setModalVarietyPrice={() => {}}
                    onClientSelected={handleClientPicked}
                    clientType={clientType}
                    setClientType={setClientType}
                    allowContinueWithoutClient={false}
                    requireClient={pendingDocumentType === 'factura'}
                    canCreateClient={canCreateClient}
                    canReadClient={canReadClient}
                    detailActionLabel="Cerrar"
                    cancelLabel="Cancelar"
                />
            </>
        )
    );
};

export default PaymentModal;
